/**
 * `.cvsm` 原生工程格式的读写（M1-15 / DESIGN.md §6.4）。
 *
 * 格式的四个设计目标（§6.4 原话："可流式读取、可增量保存、可被外部工具解析"）：
 *
 * 1. **ZIP 容器**，外部解压工具能直接打开看内容
 * 2. **manifest.json 人可读**，格式出问题时能靠肉眼定位
 * 3. **增量保存只写脏块**，改一笔不必重写整个几百 MB 的文件
 * 4. **稀疏存储**：没画过的块根本不存在，文件大小与实际内容量成正比
 *
 * 增量保存的判据是**内容哈希**，不是"脏标记"。脏标记只在本次会话里有效——
 * 打开一个已存在的文件再保存时没有任何块是脏的，但仍要知道哪些块与磁盘一致。
 *
 * 分层：io 层不认识 `Document`，所以这里收发的是 {@link CvsmDocument}
 * 这份独立的序列化契约，由应用层负责翻译。
 */
import {
  CVSM_PATHS,
  CVSM_VERSION,
  CvsmFormatError,
  encodeSegment,
  maskTileOwnerId,
  parseManifest,
  tilePath,
  type CvsmLayer,
  type CvsmLayerComp,
  type CvsmManifest,
  type CvsmTileRef,
  type TileCompression,
} from './manifest';
import {
  ZipMethod,
  crc32,
  deflateRaw,
  readEntryData,
  readZip,
  writeZip,
  zipEntry,
  type RawZipEntry,
  type ZipEntry,
} from './zip';

export {
  CVSM_PATHS,
  CVSM_VERSION,
  CvsmFormatError,
  parseManifest,
  tilePath,
  type CvsmLayer,
  type CvsmLayerComp,
  type CvsmManifest,
  type CvsmTileRef,
  type CvsmVectorFill,
  type CvsmVectorStroke,
} from './manifest';
export { ZipFormatError, crc32, readZip, writeZip } from './zip';

/** 一块像素。 */
export interface CvsmTile {
  readonly layerId: string;
  readonly lod: number;
  readonly tx: number;
  readonly ty: number;
  readonly data: Uint8Array;
}

/** 待保存的文档。 */
export interface CvsmDocument {
  readonly id: string;
  readonly size: { readonly width: number; readonly height: number };
  readonly tileSize: number;
  readonly createdAt: string;
  readonly modifiedAt: string;
  readonly root: CvsmLayer;
  /** 全部驻留的块。稀疏——没画过的块不在这里。 */
  readonly tiles: readonly CvsmTile[];
  /** 图层复合列表（M4-05）。缺省视为空数组。 */
  readonly layerComps?: readonly CvsmLayerComp[];
}

export interface SaveOptions {
  readonly compression?: TileCompression;
  readonly generator?: string;
  /**
   * 上一次保存的结果。提供它就走**增量保存**：哈希未变的块直接复用
   * 上次的压缩字节，不重新压缩也不重新算。
   */
  readonly previous?: SaveResult | null;
  /** 缩略图 PNG 字节。 */
  readonly thumbnail?: Uint8Array | null;
}

export interface SaveStats {
  /** 本次真正重新压缩并写入的块数。 */
  readonly tilesWritten: number;
  /** 从上次结果里原样复用的块数。 */
  readonly tilesReused: number;
  readonly bytes: number;
}

export interface SaveResult {
  readonly bytes: Uint8Array;
  readonly manifest: CvsmManifest;
  readonly stats: SaveStats;
  /**
   * 每个块的压缩产物，供下次增量保存复用。
   *
   * 键是容器内路径。留着它是增量保存的关键：没有它的话，
   * "块没变"也只能省下比对的时间，压缩还是要重做一遍。
   */
  readonly blobs: ReadonlyMap<string, { readonly compressed: Uint8Array; readonly ref: CvsmTileRef }>;
}

/**
 * 块内容的哈希。
 *
 * 用 FNV-1a 而不是加密哈希：这里要防的是"没改却重写"，不是恶意碰撞。
 * FNV 每字节一次乘加，几百 KB 的块也就零点几毫秒，而 SHA-256 会成为保存的瓶颈。
 * 长度一并计入，避免不同长度的数据撞上同一个值。
 */
export function hashTile(data: Uint8Array): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < data.length; i++) {
    h ^= data[i]!;
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  // 再混进长度与 CRC，把碰撞概率压到与这个用途相称的水平
  return `${h.toString(16)}-${data.length.toString(16)}-${crc32(data).toString(16)}`;
}

/* ------------------------------------------------------------------ */
/* 保存                                                                 */
/* ------------------------------------------------------------------ */

export async function saveCvsm(
  document: CvsmDocument,
  options: SaveOptions = {},
): Promise<SaveResult> {
  const compression: TileCompression = options.compression ?? 'deflate-raw';
  const previous = options.previous ?? null;

  const blobs = new Map<string, { compressed: Uint8Array; ref: CvsmTileRef }>();
  const entries: ZipEntry[] = [];
  let written = 0;
  let reused = 0;

  // 按路径排序：保存必须是**确定性的**，同样的文档要产出同样的字节。
  // 依赖调用方传入的数组顺序会让两次保存的字节不同，增量比对就失去意义
  const sorted = [...document.tiles]
    .map((tile) => ({ tile, path: tilePath(tile.layerId, tile.lod, tile.tx, tile.ty) }))
    .sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0))
    .map((entry) => entry.tile);

  for (const tile of sorted) {
    const path = tilePath(tile.layerId, tile.lod, tile.tx, tile.ty);
    const hash = hashTile(tile.data);
    const cached = previous?.blobs.get(path);

    if (cached !== undefined && cached.ref.hash === hash) {
      // 内容没变：直接复用上次的压缩结果，连压缩都省了
      blobs.set(path, cached);
      entries.push(
        zipEntry(path, cached.compressed, {
          method: compression === 'none' ? ZipMethod.Store : ZipMethod.Deflate,
          uncompressedSize: cached.ref.bytes,
          uncompressedCrc: crc32(tile.data),
        }),
      );
      reused++;
      continue;
    }

    const compressed =
      compression === 'none' ? tile.data.slice() : await deflateRaw(tile.data);
    const ref: CvsmTileRef = {
      lod: tile.lod,
      tx: tile.tx,
      ty: tile.ty,
      bytes: tile.data.length,
      hash,
    };
    blobs.set(path, { compressed, ref });
    entries.push(
      zipEntry(path, compressed, {
        method: compression === 'none' ? ZipMethod.Store : ZipMethod.Deflate,
        uncompressedSize: tile.data.length,
        uncompressedCrc: crc32(tile.data),
      }),
    );
    written++;
  }

  // 把块索引挂到各图层上
  const tilesByLayer = new Map<string, CvsmTileRef[]>();
  for (const tile of sorted) {
    const path = tilePath(tile.layerId, tile.lod, tile.tx, tile.ty);
    const ref = blobs.get(path)!.ref;
    const list = tilesByLayer.get(tile.layerId);
    if (list === undefined) tilesByLayer.set(tile.layerId, [ref]);
    else list.push(ref);
  }

  const manifest: CvsmManifest = {
    version: CVSM_VERSION,
    id: document.id,
    size: document.size,
    tileSize: document.tileSize,
    compression,
    createdAt: document.createdAt,
    modifiedAt: document.modifiedAt,
    root: attachTiles(document.root, tilesByLayer),
    ...(document.layerComps === undefined || document.layerComps.length === 0
      ? {}
      : { layerComps: document.layerComps }),
    ...(options.generator === undefined ? {} : { generator: options.generator }),
  };

  // manifest 放在**第一个条目**：流式读取时能先拿到结构再按需取块（§6.4）
  const manifestBytes = new TextEncoder().encode(`${JSON.stringify(manifest, null, 2)}\n`);
  const allEntries: ZipEntry[] = [
    zipEntry(CVSM_PATHS.manifest, manifestBytes, { method: ZipMethod.Store }),
    ...entries,
  ];
  if (options.thumbnail != null) {
    // 缩略图已经是 PNG（自带压缩），再 deflate 一遍纯属浪费
    allEntries.push(
      zipEntry(CVSM_PATHS.thumbnail, options.thumbnail, { method: ZipMethod.Store }),
    );
  }

  const bytes = writeZip(allEntries);
  return {
    bytes,
    manifest,
    stats: { tilesWritten: written, tilesReused: reused, bytes: bytes.length },
    blobs,
  };
}

/** 递归地把块索引挂到图层树上。 */
function attachTiles(layer: CvsmLayer, byLayer: ReadonlyMap<string, CvsmTileRef[]>): CvsmLayer {
  const tiles = byLayer.get(layer.id);
  const maskTiles = byLayer.get(maskTileOwnerId(layer.id));
  return {
    ...layer,
    ...(tiles === undefined ? {} : { tiles }),
    ...(maskTiles === undefined ? {} : { maskTiles }),
    ...(layer.children === undefined
      ? {}
      : { children: layer.children.map((child) => attachTiles(child, byLayer)) }),
  };
}

/* ------------------------------------------------------------------ */
/* 读取                                                                 */
/* ------------------------------------------------------------------ */

export interface LoadResult {
  readonly manifest: CvsmManifest;
  readonly tiles: readonly CvsmTile[];
  readonly thumbnail: Uint8Array | null;
}

export async function loadCvsm(bytes: Uint8Array): Promise<LoadResult> {
  const entries = readZip(bytes);
  const byName = new Map<string, RawZipEntry>();
  for (const entry of entries) byName.set(entry.name, entry);

  const manifestEntry = byName.get(CVSM_PATHS.manifest);
  if (manifestEntry === undefined) {
    throw new CvsmFormatError(`容器里没有 ${CVSM_PATHS.manifest}`);
  }
  const manifestText = new TextDecoder().decode(await readEntryData(manifestEntry));
  let parsed: unknown;
  try {
    parsed = JSON.parse(manifestText);
  } catch (error) {
    throw new CvsmFormatError(
      `manifest 不是合法 JSON：${error instanceof Error ? error.message : String(error)}`,
    );
  }
  const manifest = parseManifest(parsed);

  // 只读 manifest 里登记过的块。容器里多出来的文件一律忽略——
  // 那可能是别的工具加的，也可能是构造出来诱导我们读的
  const tiles: CvsmTile[] = [];
  for (const { ownerId, ref } of walkTileRefs(manifest.root)) {
    const path = tilePath(ownerId, ref.lod, ref.tx, ref.ty);
    const entry = byName.get(path);
    if (entry === undefined) {
      throw new CvsmFormatError(`manifest 登记了 ${path}，容器里却没有`);
    }
    if (entry.uncompressedSize !== ref.bytes) {
      throw new CvsmFormatError(
        `${path} 的大小与 manifest 不符（容器 ${entry.uncompressedSize}、manifest ${ref.bytes}）`,
      );
    }
    const data = await readEntryData(entry);
    tiles.push({ layerId: ownerId, lod: ref.lod, tx: ref.tx, ty: ref.ty, data });
  }

  const thumbnailEntry = byName.get(CVSM_PATHS.thumbnail);
  const thumbnail = thumbnailEntry === undefined ? null : await readEntryData(thumbnailEntry);

  return { manifest, tiles, thumbnail };
}

function* walkTileRefs(
  layer: CvsmLayer,
): Generator<{ ownerId: string; ref: CvsmTileRef }> {
  for (const ref of layer.tiles ?? []) yield { ownerId: layer.id, ref };
  // 蒙版块的所有者是 `<层id>#mask`（M3-08）
  for (const ref of layer.maskTiles ?? []) yield { ownerId: maskTileOwnerId(layer.id), ref };
  for (const child of layer.children ?? []) yield* walkTileRefs(child);
}

/** 容器内某个图层的块目录前缀。调试与外部工具会用到。 */
export function layerTileDir(layerId: string): string {
  return `${CVSM_PATHS.tiles}${encodeSegment(layerId)}/`;
}
